import { useEffect, useState } from "react";
import { CheckCircle2, Download, Facebook, Instagram, KeyRound, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const NET_ICON = { instagram: Instagram, facebook: Facebook, linkedin: Linkedin };
const NET_COLOR = { instagram: "text-pink-500", facebook: "text-blue-600", linkedin: "text-sky-700" };

function StatusChip({ complete, mode, network, label, testid }) {
  if (complete) {
    const txt = mode === "delegated"
      ? (network === "instagram" ? "Compte relié à votre Page Facebook" : `Accès transmis via ${label}`)
      : "Accès configuré";
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5" data-testid={testid}>
        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
        {txt}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-0.5" data-testid={testid}>
      <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
      Informations à compléter
    </span>
  );
}

function CardHeader({ n }) {
  const Icon = NET_ICON[n.network] || KeyRound;
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Icon className={`w-6 h-6 ${NET_COLOR[n.network] || "text-neutral-500"}`} />
      <div className="flex-1 min-w-0">
        <p className="font-heading font-bold text-sm">
          {n.label}{n.handle ? <span className="text-neutral-500 font-normal"> — {n.handle}</span> : ""}
        </p>
        {n.url && (
          <a href={n.url} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline truncate block" data-testid={`social-url-${n.network}`}>
            {n.url}
          </a>
        )}
      </div>
      <StatusChip complete={n.complete} mode={n.mode} network={n.network} label={n.label} testid={`social-status-${n.network}`} />
    </div>
  );
}

/** Formulaire identifiant + mot de passe (chiffré) — utilisé pour Instagram (si non relié) et LinkedIn. */
function CredentialsForm({ n, onChanged, note }) {
  const [login, setLogin] = useState(n.login || "");
  const [pw, setPw] = useState("");
  const [editPw, setEditPw] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setLogin(n.login || ""); setPw(""); setEditPw(false); }, [n.login, n.password_configured, n.delegated]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { login: login.trim() };
      if (!n.password_configured || editPw) payload.password = pw;
      await api.put(`/graphisme/espace/cm/social/${n.network}`, payload);
      toast.success(`${n.label} — accès enregistrés`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">
        Transmettre les accès {note && <span className="normal-case font-normal">— {note}</span>}
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Identifiant{n.network !== "instagram" ? " / adresse e-mail" : ""}</Label>
          <Input className="mt-1" value={login} onChange={(e) => setLogin(e.target.value)}
            placeholder={n.network === "instagram" ? "@identifiant" : "email ou identifiant"} data-testid={`social-login-${n.network}`} />
        </div>
        <div>
          <Label className="text-xs">Mot de passe</Label>
          {n.password_configured && !editPw ? (
            <div className="mt-1 flex items-center gap-2">
              <span className="flex-1 border border-neutral-200 rounded-lg px-3 py-2 text-sm tracking-widest text-neutral-500 bg-neutral-50" data-testid={`social-pw-masked-${n.network}`}>••••••••••••</span>
              <Button type="button" variant="outline" size="sm" onClick={() => { setEditPw(true); setPw(""); }} data-testid={`social-pw-edit-${n.network}`}>Modifier</Button>
            </div>
          ) : (
            <Input type="password" className="mt-1" value={pw} onChange={(e) => setPw(e.target.value)}
              placeholder="Votre mot de passe" autoComplete="new-password" data-testid={`social-password-${n.network}`} />
          )}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Button size="sm" onClick={save} disabled={busy} className="bg-neutral-900 hover:bg-neutral-800 text-white" data-testid={`social-save-${n.network}`}>
          {busy ? "Enregistrement…" : "Enregistrer"}
        </Button>
        <span className="text-[11px] text-neutral-400">Votre mot de passe est chiffré — il n'est jamais stocké ni affiché en clair.</span>
      </div>
    </div>
  );
}

/** Facebook : aucun identifiant — procédure PDF officielle + confirmation « Compte ajouté à la gestion ». */
function FacebookCard({ n, onChanged }) {
  const [busy, setBusy] = useState(false);
  const setDelegated = async (v) => {
    setBusy(true);
    try {
      await api.put("/graphisme/espace/cm/social/facebook", { delegated: v });
      toast.success(v ? "Merci ! Votre Page Facebook est rattachée à la gestion DGA." : "Confirmation retirée");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4" data-testid="social-card-facebook">
      <CardHeader n={n} />
      <div className="border border-blue-200 bg-blue-50/50 rounded-xl p-4 space-y-3" data-testid="social-delegate-box-facebook">
        <p className="text-sm font-medium">Aucun mot de passe à transmettre</p>
        <p className="text-xs text-neutral-500">
          Votre agence gère votre Page Facebook via un accès partenaire officiel (Meta Business).
          Suivez la procédure pour ajouter Digital &amp; Graphic Agency à la gestion de votre Page :
        </p>
        <ol className="text-xs text-neutral-600 space-y-1 list-none">
          <li><b>1.</b> Téléchargez la procédure ci-dessous ;</li>
          <li><b>2.</b> Suivez la manipulation pas à pas ;</li>
          <li><b>3.</b> Revenez ici et confirmez.</li>
        </ol>
        <div className="flex flex-wrap items-center gap-3">
          <a href={`${BACKEND}/api/graphisme/espace/cm/social/procedure/facebook.pdf`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs bg-white border border-blue-300 text-blue-700 rounded-lg px-3 py-2 hover:bg-blue-50 transition-colors"
            data-testid="social-pdf-facebook">
            <Download className="w-3.5 h-3.5" /> Télécharger la procédure Facebook (PDF)
          </a>
          {n.delegated ? (
            <span className="inline-flex items-center gap-2 text-xs font-medium text-green-700" data-testid="social-delegated-ok-facebook">
              <CheckCircle2 className="w-4 h-4" /> Compte Facebook ajouté à la gestion
              <button onClick={() => setDelegated(false)} disabled={busy}
                className="text-neutral-400 hover:text-red-600 underline font-normal" data-testid="social-delegated-undo-facebook">
                Annuler
              </button>
            </span>
          ) : (
            <Button size="sm" onClick={() => setDelegated(true)} disabled={busy}
              className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="social-delegated-facebook">
              {busy ? "Enregistrement…" : "Compte Facebook ajouté à la gestion"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Instagram : rien à faire si le compte est relié à la Page Facebook ; sinon identifiants chiffrés. */
function InstagramCard({ n, onChanged }) {
  const [busy, setBusy] = useState(false);
  const toggleLinked = async (v) => {
    setBusy(true);
    try {
      await api.put("/graphisme/espace/cm/social/instagram", { delegated: v });
      toast.success(v ? "Parfait — l'accès Facebook couvrira votre compte Instagram." : "Indiquez alors vos identifiants Instagram ci-dessous");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4" data-testid="social-card-instagram">
      <CardHeader n={n} />
      <div className="border border-pink-200 bg-pink-50/40 rounded-xl p-4" data-testid="social-linked-box-instagram">
        <label className="flex items-start gap-2.5 text-xs cursor-pointer" data-testid="social-delegated-wrap-instagram">
          <input type="checkbox" checked={n.delegated} disabled={busy} onChange={(e) => toggleLinked(e.target.checked)}
            className="rounded border-neutral-300 mt-0.5" data-testid="social-delegated-instagram" />
          <span>
            <b>Mon compte Instagram professionnel est relié à ma Page Facebook.</b>
            <span className="block text-neutral-500 mt-0.5">
              Dans ce cas, aucun identifiant à transmettre : l'accès Facebook (ci-dessus) suffit à votre agence.
            </span>
          </span>
        </label>
        {n.delegated && (
          <p className="text-xs font-medium text-green-700 flex items-center gap-1.5 mt-2.5" data-testid="social-delegated-ok-instagram">
            <CheckCircle2 className="w-3.5 h-3.5" /> Compte relié à votre Page Facebook — rien d'autre à faire
          </p>
        )}
      </div>
      {!n.delegated && (
        <CredentialsForm n={n} onChanged={onChanged}
          note="uniquement si votre compte Instagram n'est PAS relié à votre Page Facebook" />
      )}
    </div>
  );
}

/** LinkedIn : identifiants chiffrés OU ajout direct de DGA comme administrateur (procédure générée). */
function LinkedInCard({ n, linkedinProfile, onChanged }) {
  const toggleDelegated = async (v) => {
    try {
      await api.put("/graphisme/espace/cm/social/linkedin", { delegated: v });
      toast.success(v ? "Accès DGA confirmé — merci !" : "Attribution d'accès retirée");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4" data-testid="social-card-linkedin">
      <CardHeader n={n} />
      <CredentialsForm n={n} onChanged={onChanged} />
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-neutral-200" />
        <span className="text-[11px] font-semibold text-neutral-400">OU</span>
        <div className="h-px flex-1 bg-neutral-200" />
      </div>
      <div className="border border-blue-200 bg-blue-50/50 rounded-xl p-4 space-y-3" data-testid="social-delegate-box-linkedin">
        <p className="text-sm font-medium">Vous préférez ne pas communiquer vos identifiants ?</p>
        <p className="text-xs text-neutral-500">Vous pouvez ajouter directement DGA comme administrateur de votre Page LinkedIn.</p>
        {linkedinProfile && (
          <p className="text-[11px] text-neutral-500">Profil à ajouter : <span className="font-medium">{linkedinProfile}</span></p>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <a href={`${BACKEND}/api/graphisme/espace/cm/social/procedure/linkedin.pdf`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs bg-white border border-blue-300 text-blue-700 rounded-lg px-3 py-2 hover:bg-blue-50 transition-colors"
            data-testid="social-pdf-linkedin">
            <Download className="w-3.5 h-3.5" /> Télécharger la procédure LinkedIn
          </a>
          <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="social-delegated-wrap-linkedin">
            <input type="checkbox" checked={n.delegated} onChange={(e) => toggleDelegated(e.target.checked)}
              className="rounded border-neutral-300" data-testid="social-delegated-linkedin" />
            J'ai effectué la procédure et attribué les accès à DGA
          </label>
        </div>
        {n.delegated && (
          <p className="text-xs font-medium text-green-700 flex items-center gap-1.5" data-testid="social-delegated-ok-linkedin">
            <CheckCircle2 className="w-3.5 h-3.5" /> Accès transmis via LinkedIn
          </p>
        )}
      </div>
    </div>
  );
}

function NetworkCard({ n, linkedinProfile, onChanged }) {
  if (n.network === "facebook") return <FacebookCard n={n} onChanged={onChanged} />;
  if (n.network === "instagram") return <InstagramCard n={n} onChanged={onChanged} />;
  return <LinkedInCard n={n} linkedinProfile={linkedinProfile} onChanged={onChanged} />;
}

export function SocialNetworksPanel() {
  const [data, setData] = useState(null);
  const load = () => api.get("/graphisme/espace/cm/social").then((r) => setData(r.data)).catch(() => setData({ networks: [] }));
  useEffect(() => { load(); }, []);

  if (data === null) {
    return <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>;
  }
  const nets = data.networks || [];
  const missing = nets.filter((n) => !n.complete).length;

  return (
    <div className="space-y-4" data-testid="social-panel">
      <div>
        <h3 className="font-heading font-bold text-base">Vos accès réseaux sociaux</h3>
        <p className="text-xs text-neutral-400 mt-0.5">Réseaux gérés par votre agence dans le cadre de votre prestation Community Management.</p>
      </div>
      {nets.length === 0 && (
        <p className="text-sm text-neutral-400 bg-white border border-dashed border-neutral-300 rounded-xl p-6 text-center" data-testid="social-empty">
          Aucun réseau configuré pour le moment — votre agence définit les réseaux inclus dans votre prestation.
        </p>
      )}
      {nets.length > 0 && (
        <p className={`text-xs font-medium rounded-lg px-3 py-2 border ${missing > 0 ? "text-red-700 bg-red-50 border-red-200" : "text-green-700 bg-green-50 border-green-200"}`} data-testid="social-summary">
          {missing > 0 ? `${missing} accès réseaux sociaux reste(nt) à compléter` : "Tous vos accès sont configurés — merci !"}
        </p>
      )}
      {nets.map((n) => (
        <NetworkCard key={n.network} n={n} linkedinProfile={data.linkedin_dga_profile} onChanged={load} />
      ))}
    </div>
  );
}
